import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Card, CardContent, Typography, Grid, Chip } from "@material-ui/core";
import { isStaffUser, formatDate, createRowSelector, useTerminology } from "utils";
import { CrudOptions, Lookup } from "frame/components";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        position: "relative",
        marginBottom: theme.spacing(2),
        "&:hover": {
            cursor: "pointer",
        },
    },
    container: {
        marginTop: theme.spacing(0.5),
        marginLeft: theme.spacing(1),
    },
    content: {
        padding: theme.spacing(2),
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            flexWrap: "wrap",
        },
        "&:last-child": {
            paddingBottom: theme.spacing(3),
        },
    },
    subHeading: {
        marginTop: theme.spacing(2),
        fontWeight: "bold",
    },
    label: {
        color: "#999999",
    },
    completeChip: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
    },
    incompleteChip: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.common.white,
    },
}));

const SiteInspectionChecklistCard = ({
    siteInspectionChecklist,
    onView,
    onEdit,
    onClone,
    onDelete,
    role,
    onEmail,
}) => {
    const classes = useStyles();
    const { siteLabel } = useTerminology();
    const onRowSelect = createRowSelector(onView);
    const id = {
        id: siteInspectionChecklist.id,
        form: "site-inspection-checklist",
    };

    // Helper function to convert string boolean to actual boolean
    const getCompletedValue = (completed) => {
        if (typeof completed === 'boolean') {
            return completed;
        }
        if (typeof completed === 'string') {
            return completed.toLowerCase() === 'true';
        }
        return false;
    };

    // Determine if checklist is complete based on remedial actions
    const isComplete = siteInspectionChecklist.remedialActions && 
                      siteInspectionChecklist.remedialActions.length > 0 ? 
                      siteInspectionChecklist.remedialActions.every(action => getCompletedValue(action.completed)) : 
                      true; // If no remedial actions, consider it complete

    const status = isComplete ? "Complete" : "Incomplete";
    const statusChipClass = isComplete ? classes.completeChip : classes.incompleteChip;

    return (
        <Card className={classes.root} onClick={onRowSelect}>
            <CardContent className={classes.content}>
                <Grid className={classes.container} container spacing={2}>
                    <Grid item md={3} xs={6}>
                        <Typography variant="body2" className={classes.label}>
                            {siteLabel} Name
                        </Typography>
                        <Typography variant="h5">
                            <Lookup
                                id={siteInspectionChecklist.siteId}
                                resource="sites"
                                attribute="siteName"
                            />
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={6}>
                        <Typography variant="body2" className={classes.label}>
                            Date
                        </Typography>
                        <Typography variant="h5">
                            {formatDate(siteInspectionChecklist.date)}
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={6}>
                        <Typography variant="body2" className={classes.label}>
                            Inspector
                        </Typography>
                        <Typography variant="h5">
                            {siteInspectionChecklist.inspector || "Not Assigned"}
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={6}>
                        <Typography variant="body2" className={classes.label}>
                            Status
                        </Typography>
                        <Chip 
                            label={status} 
                            className={statusChipClass}
                            size="small"
                        />
                    </Grid>
                </Grid>
                <CrudOptions
                    id={id}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onEmail={onEmail}
                    role={role}
                    resource="site-inspection-checklist"
                    disableEdit={isStaffUser(role)}
                />
            </CardContent>
        </Card>
    );
};

SiteInspectionChecklistCard.propTypes = {
    siteInspectionChecklist: PropTypes.object.isRequired,
    onView: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onClone: PropTypes.func,
    onDelete: PropTypes.func.isRequired,
    role: PropTypes.string.isRequired,
    onEmail: PropTypes.func.isRequired,
};

export default SiteInspectionChecklistCard; 